import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Dropdown, Form, Input, Label, TextArea } from 'semantic-ui-react'
import { ChatCircleText } from 'phosphor-react'
import styled from 'styled-components'
import { QuestionThreadService } from 'utility/services/question-threads'
import {
    MAX_THREAD_TEXT,
    MESSAGE_KIND,
    THREAD_STATUS,
    answerChangeKind,
    answerOptions,
    buildClosePayload,
    buildCommentPayload,
    buildReplyPayload,
    currentOptionIds,
    currentValue,
    formatThreadTime,
    gainedAnswerChange,
    messageHeadline,
    statusInfo,
    threadAnchorId,
    threadErrorMessage,
    validateMessage,
} from 'utility/question-threads'
import { useNotification } from 'hooks/toast'
import { useQuestionThreads } from './QuestionThreadsContext'
import { ThreadAttachmentPicker, ThreadFiles } from './ThreadAttachments'

const Panel = styled.div`
    margin-top: 12px;
    border: 1px solid ${({ $focused }) => ($focused ? '#1b2587' : '#e5e7eb')};
    box-shadow: ${({ $focused }) => ($focused ? '0 0 0 3px rgba(27, 37, 135, 0.15)' : 'none')};
    border-radius: 8px;
    background: #fafafa;
    padding: 10px 12px;
    transition: box-shadow 0.3s, border-color 0.3s;
    .qt-head {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        font-weight: 600;
        color: #1f2937;
    }
    .qt-msg {
        border-top: 1px solid #eee;
        padding: 8px 0;
    }
    .qt-msg:first-of-type {
        border-top: none;
    }
    .qt-meta {
        font-size: 12px;
        color: #6b7280;
    }
    .qt-who {
        font-weight: 600;
        color: #111827;
    }
    .qt-system {
        font-style: italic;
        color: #4b5563;
    }
    .qt-text {
        white-space: pre-wrap;
        word-break: break-word;
        margin-top: 2px;
    }
    .qt-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 8px;
    }
    .qt-closed {
        font-size: 12px;
        color: #166534;
        margin-top: 4px;
    }
`

const AddCommentLink = styled.button`
    margin-top: 10px;
    border: none;
    background: none;
    padding: 0;
    color: #1b2587;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    &:hover {
        text-decoration: underline;
    }
`

const isSystemKind = kind =>
    kind === MESSAGE_KIND.AnswerChanged || kind === MESSAGE_KIND.Closed || kind === MESSAGE_KIND.Reopened

const ThreadMessage = ({ message }) => {
    const system = isSystemKind(message.Kind)
    const headline = messageHeadline(message)
    return (
        <div className="qt-msg">
            <div className="qt-meta">
                {system ? (
                    <span className="qt-system">{headline}</span>
                ) : (
                    <span className="qt-who">{message.UserName || 'Someone'}</span>
                )}
                {system && message.Kind === MESSAGE_KIND.AnswerChanged && message.UserName ? ` - ${message.UserName}` : ''}
                {message.Kind === MESSAGE_KIND.Reply && !system ? ' (reply)' : ''}
                {formatThreadTime(message.CreatedOnUtc) ? ` · ${formatThreadTime(message.CreatedOnUtc)}` : ''}
            </div>
            {message.Text ? <div className="qt-text">{message.Text}</div> : null}
            <ThreadFiles files={message.Files} />
        </div>
    )
}

const MODE_TEXT = {
    comment: { placeholder: 'Ask the department (e.g. "Show me the log").', submit: 'Add comment' },
    reply: { placeholder: 'Write your reply...', submit: 'Reply' },
    reopen: { placeholder: 'Why is it reopened?', submit: 'Reopen' },
}

/** Text + files (+ answer change for replies) for one action; onSubmit(payload) returns a promise. */
const ThreadComposer = ({ mode, item, answerChangeAllowed, onSubmit, onCancel }) => {
    const { errorToast } = useNotification()
    const [text, setText] = useState('')
    const [files, setFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [saving, setSaving] = useState(false)
    const changeKind = mode === 'reply' && answerChangeAllowed ? answerChangeKind(item) : null
    const [changeOn, setChangeOn] = useState(false)
    const [optionIds, setOptionIds] = useState(() => currentOptionIds(item))
    const [value, setValue] = useState(() => currentValue(item))
    const texts = MODE_TEXT[mode] || MODE_TEXT.comment
    const mountedRef = useRef(true)
    useEffect(
        () => () => {
            mountedRef.current = false
        },
        [],
    )

    let answerChange = null
    if (changeOn && changeKind) {
        answerChange = changeKind.startsWith('options') ? { optionIds } : { value }
    }

    const submit = async () => {
        const problem = validateMessage({ mode, text, files, answerChange })
        if (problem) {
            errorToast(problem)
            return
        }
        const payload = mode === 'reply' ? buildReplyPayload({ text, files, answerChange }) : buildCommentPayload({ text, files })
        setSaving(true)
        try {
            const ok = await onSubmit(payload)
            // the composer may be gone after a successful save (e.g. the reopen box closes)
            if (ok && mountedRef.current) {
                setText('')
                setFiles([])
                setChangeOn(false)
            }
        } finally {
            if (mountedRef.current) setSaving(false)
        }
    }

    const options = changeKind && changeKind.startsWith('options') ? answerOptions(item) : []

    return (
        <Form style={{ marginTop: 8 }} onSubmit={e => e.preventDefault()}>
            <TextArea
                rows={2}
                value={text}
                maxLength={MAX_THREAD_TEXT}
                placeholder={texts.placeholder}
                disabled={saving}
                onChange={(_, d) => setText(d.value || '')}
            />
            {changeKind && (
                <div style={{ marginTop: 8 }}>
                    <Checkbox
                        label="Also change the answer"
                        checked={changeOn}
                        disabled={saving}
                        onChange={(_, d) => setChangeOn(!!d.checked)}
                    />
                    {changeOn && changeKind.startsWith('options') && (
                        <Dropdown
                            selection
                            fluid
                            multiple={changeKind === 'options-multi'}
                            placeholder="New answer"
                            options={options}
                            value={changeKind === 'options-multi' ? optionIds : optionIds[0] || null}
                            onChange={(_, d) => {
                                if (Array.isArray(d.value)) setOptionIds(d.value)
                                else setOptionIds(d.value ? [d.value] : [])
                            }}
                            style={{ marginTop: 6 }}
                        />
                    )}
                    {changeOn && !changeKind.startsWith('options') && (
                        <Input
                            fluid
                            type={changeKind === 'number' ? 'number' : 'text'}
                            placeholder="New answer"
                            value={value}
                            onChange={(_, d) => setValue(d.value || '')}
                            style={{ marginTop: 6 }}
                        />
                    )}
                </div>
            )}
            <div className="qt-actions">
                <ThreadAttachmentPicker files={files} onChange={setFiles} disabled={saving} onUploadingChange={setUploading} />
                <Button type="button" size="small" primary loading={saving} disabled={saving || uploading} onClick={submit}>
                    {texts.submit}
                </Button>
                {onCancel && (
                    <Button type="button" size="small" basic disabled={saving || uploading} onClick={onCancel}>
                        Cancel
                    </Button>
                )}
            </div>
        </Form>
    )
}

/**
 * Review thread under one inspection question (rendered inside the question card). Shows nothing unless the
 * template's review flow has question comments. Buttons follow the server's Can* flags; read-only users see the
 * thread only.
 */
export const QuestionThreadSlot = ({ item }) => {
    const ctx = useQuestionThreads()
    const { errorToast, successToast } = useNotification()
    const [composer, setComposer] = useState(null) // 'comment' | 'reopen' (the reply box is always open)
    const [closing, setClosing] = useState(false)
    const slotMountedRef = useRef(true)
    useEffect(
        () => () => {
            slotMountedRef.current = false
        },
        [],
    )

    if (!ctx.available || !item || item.Id == null) return null
    const thread = ctx.map[String(item.Id)]
    const anchorId = threadAnchorId(item.Id)

    const fail = error => {
        const message = threadErrorMessage(error)
        if (message) errorToast(message)
        if (error?.status === 409) ctx.reload()
        return false
    }

    const afterSave = (response, sentAnswerChange = false) => {
        const before = thread
        if (response?.Thread) ctx.applyThread(response.Thread)
        else ctx.reload()
        // reload the inspection only when a reply sent an answer change and the thread really gained that message
        if (sentAnswerChange && response?.Thread && gainedAnswerChange(before, response.Thread)) {
            ctx.answerChanged()
            successToast('Reply sent and the answer changed.')
        }
        if (slotMountedRef.current) setComposer(null)
        return true
    }

    const openOrComment = payload =>
        QuestionThreadService.comment(ctx.auditId, item.Id, payload)
            .then(r => afterSave(r))
            .catch(fail)

    if (!thread) {
        if (!ctx.canComment) return <span id={anchorId} />
        return (
            <div id={anchorId}>
                {composer === 'comment' ? (
                    <Panel>
                        <div className="qt-head">
                            <ChatCircleText size={16} /> New comment
                        </div>
                        <ThreadComposer mode="comment" item={item} onSubmit={openOrComment} onCancel={() => setComposer(null)} />
                    </Panel>
                ) : (
                    <AddCommentLink type="button" onClick={() => setComposer('comment')}>
                        <ChatCircleText size={16} /> Add comment
                    </AddCommentLink>
                )}
            </div>
        )
    }

    const info = statusInfo(thread.Status)
    const isClosed = thread.Status === THREAD_STATUS.Closed

    const reply = payload =>
        QuestionThreadService.reply(thread.ThreadId, payload)
            .then(r => afterSave(r, !!payload.AnswerChange))
            .catch(fail)

    const reopen = payload =>
        QuestionThreadService.reopen(thread.ThreadId, payload)
            .then(r => afterSave(r))
            .catch(fail)

    const close = () => {
        setClosing(true)
        QuestionThreadService.close(thread.ThreadId, buildClosePayload({}))
            .then(r => afterSave(r))
            .catch(fail)
            .finally(() => slotMountedRef.current && setClosing(false))
    }

    return (
        <Panel id={anchorId} $focused={ctx.focusedQuestionId === Number(item.Id)}>
            <div className="qt-head">
                <ChatCircleText size={16} />
                Review comments
                <Label size="tiny" color={info.color} title={info.hint}>
                    {info.text}
                </Label>
                {thread.Round > 1 && <span className="qt-meta">Round {thread.Round}</span>}
                {!isClosed && <span className="qt-meta">{info.hint}</span>}
            </div>
            {thread.CurrentAnswer && (
                <div className="qt-meta" style={{ marginTop: 4 }}>
                    Current answer: <span className="qt-who">{thread.CurrentAnswer}</span>
                </div>
            )}
            <div style={{ marginTop: 6 }}>
                {thread.Messages.map(m => (
                    <ThreadMessage key={m.MessageId || `${m.Kind}-${m.CreatedOnUtc}`} message={m} />
                ))}
            </div>
            {isClosed && thread.ClosedByName && (
                <div className="qt-closed">
                    Closed by {thread.ClosedByName}
                    {formatThreadTime(thread.ClosedOnUtc) ? ` on ${formatThreadTime(thread.ClosedOnUtc)}` : ''}
                </div>
            )}
            {!isClosed && thread.CanReply && (
                <ThreadComposer mode="reply" item={item} answerChangeAllowed={ctx.answerChangeAllowed} onSubmit={reply} />
            )}
            {composer === 'comment' && (
                <ThreadComposer mode="comment" item={item} onSubmit={openOrComment} onCancel={() => setComposer(null)} />
            )}
            {composer === 'reopen' && (
                <ThreadComposer mode="reopen" item={item} onSubmit={reopen} onCancel={() => setComposer(null)} />
            )}
            {!composer && (
                <div className="qt-actions">
                    {!isClosed && thread.CanComment && (
                        <Button type="button" size="small" basic onClick={() => setComposer('comment')}>
                            Add comment
                        </Button>
                    )}
                    {!isClosed && thread.CanClose && (
                        <Button type="button" size="small" color="green" basic loading={closing} disabled={closing} onClick={close}>
                            Close comment
                        </Button>
                    )}
                    {isClosed && thread.CanReopen && (
                        <Button type="button" size="small" basic onClick={() => setComposer('reopen')}>
                            Reopen
                        </Button>
                    )}
                </div>
            )}
        </Panel>
    )
}
